// Shift chips in chat wear the calendar's status palette, so a mentioned
// shift reads as the same object the calendar shows: purple while a fill
// outreach is drafted or running, red while open, quiet neutral once
// settled. The classes come straight from CALENDAR_CHIP_PALETTE so the two
// surfaces can never drift apart.
import { resolveShiftStatus } from '@apps/web/components/dashboard/shift_list_filters';
import {
    type EntityReferenceScope,
    type ResolvedShiftReference,
    useResolvedEntityReference,
} from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import {
    CALENDAR_CHIP_PALETTE,
    type CalendarChipBucket,
    resolveCalendarStatusBucket,
} from '@apps/web/lib/shift_calendar_palette';
import {
    chipClassForBucket,
    TINT_TRANSITION_CLASS,
} from '@apps/web/lib/shift_reference_chip_style';
import { cn } from '@apps/web/lib/utils';

export function shiftReferenceBucket(
    shift: Pick<
        ResolvedShiftReference,
        'status' | 'latestStage' | 'hasActiveFillOutreach'
    >,
): CalendarChipBucket {
    const statusBucket = resolveCalendarStatusBucket(
        resolveShiftStatus(shift.status, shift.latestStage),
    );
    // Settled EHR reality never yields to outreach state — the same
    // precedence as the calendar's getCalendarDisplayBucket.
    if (
        statusBucket === 'cancelled' ||
        statusBucket === 'completed' ||
        statusBucket === 'in_progress' ||
        statusBucket === 'clock_issue'
    ) {
        return statusBucket;
    }
    return shift.hasActiveFillOutreach ? 'outreaching' : statusBucket;
}

/**
 * The calendar-palette classes for a shift chip, or `null` for other entity
 * types (whose chips keep the static entity tints). A shift whose status
 * has not resolved yet wears the calendar's quiet neutral treatment.
 */
export function useShiftReferenceChipClass(
    type: ReferenceType,
    id: string,
    /** For hosts outside a run harness (the sidebar's new-chat composer),
     * which know their organization without a `HarnessScope`. */
    scopeOverride?: EntityReferenceScope,
): string | null {
    const harnessScope = useHarnessScope();
    const scope = harnessScope ?? scopeOverride ?? null;
    // A null scope keeps non-shift ids out of the resolve batches; only
    // shift chips need status data here.
    const reference = useResolvedEntityReference(
        type === 'shift' ? scope : null,
        type,
        id,
    );
    if (type !== 'shift') {
        return null;
    }
    if (reference === null || reference.kind !== 'shift') {
        return chipClassForBucket('scheduled');
    }
    return chipClassForBucket(shiftReferenceBucket(reference));
}

/**
 * The calendar accent color for a standalone shift icon (the mention
 * picker rows), from the same status bucket as the chip tint — `null` for
 * other entity types. Uses the palette's eyebrow tone, the color the
 * calendar itself uses for icon-sized status accents.
 */
export function useShiftReferenceIconClass(
    type: ReferenceType,
    id: string,
    scopeOverride?: EntityReferenceScope,
): string | null {
    const harnessScope = useHarnessScope();
    const scope = harnessScope ?? scopeOverride ?? null;
    const reference = useResolvedEntityReference(
        type === 'shift' ? scope : null,
        type,
        id,
    );
    if (type !== 'shift') {
        return null;
    }
    const bucket =
        reference !== null && reference.kind === 'shift'
            ? shiftReferenceBucket(reference)
            : 'scheduled';
    return cn(
        CALENDAR_CHIP_PALETTE[bucket].eyebrowClassName,
        TINT_TRANSITION_CLASS,
    );
}
